import type { FamilyFactAction } from "@/lib/rememberFamilyFact";

/**
 * What to SAY TO HER when a family-fact write is refused.
 *
 * ⚠️ Do not reach for `RememberFamilyFactResult.error` here. Its own type says
 * "Model-facing, already phrased for Kin to speak" — every string in it is
 * second-person instruction addressed to the realtime voice model, not to a
 * person:
 *
 *   rejected_medical  "You don't keep medication dosages or diagnoses. Tell her
 *                      that warmly, in your own words, and don't retry."
 *   rejected_category "That doesn't fit any of the things you can remember.
 *                      Don't retry — just move on."
 *   (generic)         "Couldn't save that. Tell her honestly and don't retry."
 *
 * #1508 briefly rendered those verbatim in a toast, so a mom typing a
 * medication dose into Family Profile would have been told to tell herself
 * something warmly and not retry. Caught in review by a peer session before it
 * merged. That is the same defect class #1481 exists to fix — advice in a toast
 * she cannot act on — reintroduced one layer down while fixing it.
 *
 * Kept in its own module, like proSetupFailureMessage.ts, so the wording is
 * testable without mounting a large component.
 *
 * Brand voice: warm, plain, no jargon, no em-dashes, and never advice to retry
 * something that cannot succeed.
 */
/**
 * #1857 — what to SAY TO HER when the helper could not find out whether a
 * write landed (`action === "unverified"`). Shared by every surface that
 * toasts over a family-fact write, so "couldn't confirm" is one sentence
 * everywhere and never drifts into "didn't save".
 *
 * `what` is the thing in her words — "that", "that birthday" — never a key.
 */
export function familyFactUnverifiedCopy(what = "that"): string {
  return `Couldn't confirm ${what} saved. Check back in a sec.`;
}

export function familyFactAddErrorCopy(action: FamilyFactAction | undefined): string {
  switch (action) {
    case "unverified":
      // #1857 — the write's outcome could not be read, so this must not say
      // "didn't": the fact may well be there. The one case that may suggest
      // looking again, because looking is what settles it.
      return familyFactUnverifiedCopy();
    case "rejected_medical":
      // ⚠️ "Kin doesn't keep medication doses or diagnoses" was FALSE and is
      // fixed here. Kin does keep them: the Prescription Refill tracker stores
      // medication_name, dosage, doses_per_day and pharmacy details in
      // scheduled_tasks (TasksTab's prescription-parse flow), nowhere near this
      // guard. The refusal is specific to the family_context memory table, and
      // its own rationale is about what the MODEL recalls unprompted.
      //
      // So this points her at the surface that does hold it, instead of telling
      // her the product cannot do something it does. Deliberately not vague:
      // she asked Kin to remember a dose, and the honest answer is where it
      // actually lives.
      return "Medication details live in your Prescription Refill tracker.";
    case "rejected_category":
      // The category comes from the group she is adding under, so she cannot
      // fix this by retyping. Saying "try again" would be the #1481 defect.
      return "Kin can't file that one here.";
    case "rejected_empty":
      return "That came through empty.";
    case "rejected_rate_limit":
      // The one refusal that IS temporary, so it is the one that may suggest
      // waiting. Still not "try again" — a immediate retry hits the same cap.
      return "You've saved a lot just now. Give it a few minutes.";
    default:
      return "Couldn't add that.";
  }
}
